import { format, parseISO } from 'date-fns';

export const formatDate = (dateString: string) => {
  try {
    const date = parseISO(dateString);
    return format(date, 'MMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

export const formatDateTime = (dateString: string) => {
  try {
    const date = parseISO(dateString);
    return format(date, 'MMM d, yyyy h:mm a');
  } catch (error) {
    console.error('Error formatting date time:', error);
    return dateString;
  }
};

export const formatTimeForDisplay = (date: Date | undefined): string => {
  if (!date) return '';
  return format(date, 'HH:mm:ss');
};

export const convertTimeStringToDate = (timeString: string): Date => {
  // Convert a time string like "13:45:00" to a Date object
  const [hours, minutes, seconds] = timeString.split(':').map(Number);
  const date = new Date();
  date.setHours(hours || 0);
  date.setMinutes(minutes || 0);
  date.setSeconds(seconds || 0);
  return date;
};

export const formatPhoneNumber = (value: string): string => {
  // Return empty string for empty input
  if (!value) return '';
  
  // Remove all non-digits
  const cleaned = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (cleaned.length >= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  } else if (cleaned.length >= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length >= 3) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  }
  return cleaned;
};

/**
 * Formats a number as currency with the given currency code
 * @param amount The amount to format (can be in dollars or cents)
 * @param currency The currency code (defaults to USD)
 * @returns A formatted currency string
 */
export const formatCurrency = (amount: number, currency = 'USD'): string => {
  // Ensure amount is a valid number
  if (isNaN(amount)) return '$0.00';
  
  // Format with appropriate currency symbol
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * Parse a currency string to cents (integer)
 * Handles strings like "$10.00", "10.00", or "10" and converts to cents (1000)
 */
export const parseCurrencyToCents = (currencyStr: string): number => {
  if (!currencyStr) return 0;
  
  // Remove currency symbol and any non-numeric characters except decimal point
  const cleanedValue = currencyStr.replace(/[^\d.]/g, '');
  
  // Parse as float (dollars with cents)
  const floatValue = parseFloat(cleanedValue);
  
  // If parsing failed or result is NaN, return 0
  if (isNaN(floatValue)) return 0;
  
  // Convert dollars to cents (multiply by 100 and round to avoid floating point issues)
  return Math.round(floatValue * 100);
};

/**
 * Format cents as a dollar amount string without currency symbol
 * @param cents Amount in cents
 * @returns Formatted string like "10.00"
 */
export const formatCentsToDecimal = (cents: number): string => {
  if (isNaN(cents)) return '0.00';
  return (cents / 100).toFixed(2);
};
